'use client';

import { useCallback, useRef } from 'react';
import i18n, { initI18n } from '@/lib/i18n/config';
import {
  cacheLanguageFromUserSettings,
  clearAppLanguageStorage,
  getCachedAppLanguage,
  setCachedAppLanguage,
} from '@/lib/i18n/language-storage';
import type { AppLanguage } from '@/lib/i18n/constants';
import { normalizeLanguage } from '@/lib/i18n/constants';
import {
  isPublicLoginRoute,
} from '@/lib/i18n/public-serbian-routes';
import { getLandingLanguage } from '@/lib/i18n/landing-language-storage';
import { apiClient } from '@/lib/api-client';
import { getAccessToken, hasStoredUser } from '@/lib/auth-storage';
import { broadcastUserSettingsUpdate } from '@/contexts/UserSettingsContext';

/** @deprecated Use getCachedAppLanguage — kept for gradual migration */
export function getStoredGuestLanguage(): AppLanguage {
  return getCachedAppLanguage();
}

export { clearAppLanguageStorage };

function syncDocumentLanguage(lang: AppLanguage) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
}

/** Apply i18n + optional localStorage cache (guest UI or mirror of API language). */
export function applyAppLanguage(lang: AppLanguage, options?: { cache?: boolean }) {
  let normalized = normalizeLanguage(lang);
  let cache = options?.cache;
  if (typeof window !== 'undefined' && isPublicLoginRoute(window.location.pathname)) {
    normalized = getLandingLanguage();
    cache = false;
  }
  if (cache !== false && typeof window !== 'undefined') {
    setCachedAppLanguage(normalized);
  }
  initI18n(normalized);
  syncDocumentLanguage(normalized);
  if (i18n.isInitialized && normalizeLanguage(i18n.language) !== normalized) {
    i18n.language = normalized;
    i18n.emit('languageChanged', normalized);
  }
}

export function applyLanguageFromUserSettings(language: string | null | undefined): AppLanguage {
  const lang = cacheLanguageFromUserSettings(language);
  if (typeof window !== 'undefined' && isPublicLoginRoute(window.location.pathname)) {
    applyAppLanguage(getLandingLanguage(), { cache: false });
    return lang;
  }
  applyAppLanguage(lang, { cache: true });
  return lang;
}

export function useAppLanguage() {
  const persistInFlight = useRef(false);

  const setLanguage = useCallback(async (lang: AppLanguage, options?: { persistToApi?: boolean }) => {
    const normalized = normalizeLanguage(lang);
    applyAppLanguage(normalized, { cache: true });

    if (!options?.persistToApi || typeof window === 'undefined') return;
    if (!(getAccessToken() || hasStoredUser()) || persistInFlight.current) return;

    try {
      persistInFlight.current = true;
      await apiClient.updateUserSettings({ language: normalized });
      broadcastUserSettingsUpdate();
    } catch (error) {
      console.error('Failed to persist language preference:', error);
    } finally {
      persistInFlight.current = false;
    }
  }, []);

  return {
    language: normalizeLanguage(i18n.language),
    setLanguage,
  };
}
